import { Bundle, QuestionnaireResponse, QuestionnaireResponseItem, Resource } from 'fhir/r4';
import { ArrivalBoardColumn, ArrivalBoardColumnSource } from '../entities/arrival-board-configuration';
import { ArrivalBoardItem } from '../entities/arrival-board-item';

const RESPONSE_PATH_PREFIX = 'QuestionnaireResponse.';

type CellValue = string | number | boolean | undefined;

type Answer = NonNullable<QuestionnaireResponseItem['answer']>[number];

/**
 * Turns one polled searchset into board rows. The Bundle holds QuestionnaireResponses only; `subject`
 * dangles and must never be followed. Two behaviours are load-bearing:
 *
 * - ⚠ **Newest-per-caseId collapse** (first seen wins, the Bundle being newest-first): a duplicate id
 *   would otherwise give two rows the same diff key and corrupt the differ's tracking.
 * - ⚠ **Rows come back oldest-first**: the differ ranks later rows higher. Fix ordering here, never in
 *   the differ, whose animations depend on it.
 */
export function mapBundleToItems(bundle: Bundle, columns: ArrivalBoardColumn[]): ArrivalBoardItem[] {
    const rows: ArrivalBoardItem[] = [];
    const seenCaseIds = new Set<string>();

    for (const entry of bundle?.entry ?? []) {
        if (entry.search?.mode !== 'match') continue;
        const response = entry.resource;
        if (!isQuestionnaireResponse(response)) continue;

        // The resource id is the amber missionId, stable across amendments.
        const caseId = response.id;
        if (caseId === undefined) continue;
        if (seenCaseIds.has(caseId)) continue;

        seenCaseIds.add(caseId);
        rows.push(buildRow(caseId, response, columns));
    }

    return rows.reverse();
}

function isQuestionnaireResponse(resource: Resource | undefined): resource is QuestionnaireResponse {
    return resource?.resourceType === 'QuestionnaireResponse';
}

function buildRow(caseId: string, response: QuestionnaireResponse, columns: ArrivalBoardColumn[]): ArrivalBoardItem {
    const row: ArrivalBoardItem = { caseId };
    for (const column of columns) {
        row[column.key] =
            column.source === ArrivalBoardColumnSource.RESPONSE
                ? readResponseCell(response, column.path, caseId)
                : readAnswerCell(response.item, column.linkId);
    }
    // Re-stamped last: a column keyed 'caseId' must not be able to blank the diff key.
    row.caseId = caseId;
    return row;
}

function readResponseCell(response: QuestionnaireResponse, path: string | undefined, caseId: string): CellValue {
    if (!path) return undefined;
    const element = path.startsWith(RESPONSE_PATH_PREFIX) ? path.slice(RESPONSE_PATH_PREFIX.length) : path;
    if (element === 'id') return caseId;
    return asCellValue((response as unknown as Record<string, unknown>)[element]);
}

function readAnswerCell(items: QuestionnaireResponseItem[] | undefined, linkId: string | undefined): CellValue {
    if (!linkId) return undefined;
    const answer = findItem(items, linkId)?.answer?.[0];
    return answer ? flattenAnswer(answer) : undefined;
}

function findItem(
    items: QuestionnaireResponseItem[] | undefined,
    linkId: string,
): QuestionnaireResponseItem | undefined {
    for (const item of items ?? []) {
        if (item.linkId === linkId) return item;
        const nested = findItem(item.item, linkId);
        if (nested) return nested;
    }
    return undefined;
}

function flattenAnswer(answer: Answer): CellValue {
    if (answer.valueCoding) return answer.valueCoding.display ?? answer.valueCoding.code;
    return (
        answer.valueString ??
        answer.valueInteger ??
        answer.valueDecimal ??
        answer.valueBoolean ??
        answer.valueDate ??
        answer.valueDateTime ??
        answer.valueTime ??
        answer.valueUri
    );
}

/** Guard the row's index signature: anything non-primitive would render as '[object Object]'. */
function asCellValue(value: unknown): CellValue {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : undefined;
}
